// A dashboard build is tracked as a task row, but it runs service-side with no agent conversation behind
// it, so the task drill-in shows what the BUILD produced instead of a transcript. The row's marker is
// `display.dashboardBuild`: the harness stamps `{ buildId, dashboard }` when it opens the row, and the VMR
// builder adds `result` when the run ends (`BuildTaskSettler`):
//   { runId, status, outcome, endedAt, summary, cards: [{ dashboard, slug, title, state }] }
// `result.runId` is the run that actually ended, which differs from `buildId` for a build the reviver
// brought back after an instance shutdown. Old rows carry no `result`.
//
// Pure, so the same reading is pinned in a unit test; iOS `DashboardBuildResult` and Android
// `DashboardBuildResult` mirror it one to one (same fields, same states, same i18n keys).

export type DashboardBuildCardState = 'published' | 'draft' | 'refused';

export interface DashboardBuildCard {
  dashboard: string;
  slug: string;
  title: string;
  state: DashboardBuildCardState;
}

export interface DashboardBuildResult {
  runId: string;
  status: string;
  outcome: string;
  endedAt: string;
  /** The build's own final text, verbatim: it is in whatever language the run wrote it. */
  summary: string;
  cards: DashboardBuildCard[];
}

export interface DashboardBuildMarker {
  /** The run the row was opened for. Prefer `result.runId` for the run that ended. */
  buildId?: string;
  /** The dashboard slug the build targets: what "Open dashboard" opens (`dashboard/<slug>`). */
  dashboard?: string;
  result?: DashboardBuildResult;
}

const CARD_STATES: readonly DashboardBuildCardState[] = ['published', 'draft', 'refused'];

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** `display.dashboardBuild`, normalized. Undefined when the row is not a dashboard build. A legacy boolean
 *  marker reads as a build with nothing else known. */
export function parseDashboardBuild(display: unknown): DashboardBuildMarker | undefined {
  if (!display || typeof display !== 'object') return undefined;
  const raw = (display as Record<string, unknown>).dashboardBuild;
  if (raw === undefined || raw === null || raw === false) return undefined;
  if (typeof raw !== 'object') return {};
  const marker = raw as Record<string, unknown>;
  const out: DashboardBuildMarker = {};
  if (str(marker.buildId)) out.buildId = str(marker.buildId);
  if (str(marker.dashboard)) out.dashboard = str(marker.dashboard);
  const result = marker.result;
  if (result && typeof result === 'object') {
    const r = result as Record<string, unknown>;
    const cards = Array.isArray(r.cards) ? r.cards : [];
    out.result = {
      runId: str(r.runId),
      status: str(r.status),
      outcome: str(r.outcome),
      endedAt: str(r.endedAt),
      summary: str(r.summary).trim(),
      cards: cards.flatMap((c): DashboardBuildCard[] => {
        if (!c || typeof c !== 'object') return [];
        const card = c as Record<string, unknown>;
        const slug = str(card.slug);
        if (!slug) return [];
        const state = CARD_STATES.find((s) => s === card.state) ?? 'draft';
        return [{ dashboard: str(card.dashboard), slug, title: str(card.title) || slug, state }];
      }),
    };
  }
  return out;
}

/** The i18n key for a card's state label. */
export const CARD_STATE_KEY: Record<DashboardBuildCardState, string> = {
  published: 'tasks.detail.buildResult.state.published',
  draft: 'tasks.detail.buildResult.state.draft',
  refused: 'tasks.detail.buildResult.state.refused',
};

/** The change-log labels the two settling writers use (VMR `BuildTaskSettler`, harness `dashboard-build-task.ts`). */
const LABEL_KEY: Record<string, string> = {
  'dashboard-build-published': 'tasks.detail.buildResult.published',
  'dashboard-build-failed': 'tasks.detail.buildResult.failed',
  'dashboard-build-stale': 'tasks.detail.buildResult.stale',
  'dashboard-build-superseded': 'tasks.detail.buildResult.superseded',
};

const STATUS_KEY: Record<string, string> = {
  COMPLETED: 'tasks.detail.buildResult.published',
  FAILED: 'tasks.detail.buildResult.failed',
  MOOT: 'tasks.detail.buildResult.superseded',
};

export type DashboardBuildView =
  | { kind: 'building'; dashboard?: string }
  | {
      kind: 'finished';
      /** The change-log line, localized by its label. Absent when a summary of a delivered build says it. */
      messageKey?: string;
      summary?: string;
      cards: DashboardBuildCard[];
      /** The dashboard "Open dashboard" opens, or absent when the row names none. */
      dashboard?: string;
    };

/** Whether the row is still being built: the two statuses a build task holds before it settles. */
export function isBuildRunning(status: string | undefined): boolean {
  const s = (status ?? '').toUpperCase();
  return s === 'IN_PROGRESS' || s === 'PENDING';
}

/**
 * What the task screen shows for a dashboard build. Running → "Building your dashboard…". Finished → the
 * build's summary and the cards it wrote; a FAILED/MOOT row, or an old row with no `result`, also leads with
 * its latest build change-log line, so a finished build never shows an empty state.
 */
export function dashboardBuildView(
  status: string | undefined,
  marker: DashboardBuildMarker | undefined,
  changeLog: readonly { label?: string }[] = [],
): DashboardBuildView {
  const dashboard = marker?.dashboard || marker?.result?.cards.find((c) => c.dashboard)?.dashboard || undefined;
  if (isBuildRunning(status)) return { kind: 'building', dashboard };
  const result = marker?.result;
  const s = (status ?? '').toUpperCase();
  const summary = result?.summary || undefined;
  const cards = result?.cards ?? [];
  const latestLabel = [...changeLog].reverse().find((c) => !!c.label && LABEL_KEY[c.label])?.label;
  const lineKey = (latestLabel && LABEL_KEY[latestLabel]) || STATUS_KEY[s] || 'tasks.detail.buildResult.finished';
  const needsLine = !result || s === 'FAILED' || s === 'MOOT' || (!summary && cards.length === 0);
  return { kind: 'finished', ...(needsLine ? { messageKey: lineKey } : {}), ...(summary ? { summary } : {}), cards, dashboard };
}

/** The in-app path "Open dashboard" goes to: the `dashboard/<slug>` deeplink's web route (see `lib/app-link.ts`). */
export function dashboardPath(slug: string): string {
  return `/pages/${encodeURIComponent(slug)}`;
}
